import { getSystemSettings, updateSystemSettings } from '@/api/systemSettings.js';
import {
    INPUT_TYPES,
    RANGE_CONSTRAINTS,
    STEP_VALUES,
    STRING_CONSTRAINTS,
    KEY_PATTERNS,
    DESCRIPTION_PATTERNS,
    DEFAULT_SETTINGS,
} from '@/constants/systemSettingsConstants.js';

// Business-friendly Russian labels per setting key (managers should not read
// "Opensearch Vector Min Score"). Unmapped keys fall back to the auto Title Case.
const RU_LABELS = {
    free_tier_daily_downloads_limit: 'Лимит скачиваний в день (бесплатный тариф)',
    enable_query_decomposition: 'Разбор сложных запросов',
    query_decomposition_threshold: 'Порог понятий для разбора запроса',
    query_decomposition_cache_ttl_seconds: 'Срок хранения кэша разбора (сек)',
    enable_query_decomposition_cache: 'Кэширование разбора запросов',
    opensearch_vector_k: 'Размер пула кандидатов',
    opensearch_vector_timeout_ms: 'Таймаут поиска (мс)',
    opensearch_vector_min_score: 'Минимальная похожесть в выдаче',
    opensearch_rescore_oversample_factor: 'Коэффициент пересортировки',
    tag_filter_mode: 'Строгость совпадения по тегам',
    full_query_boost: 'Приоритет полного совпадения запроса',
    modified_phrase_boost: 'Приоритет совпадений по близким фразам',
    unmodified_entity_boost: 'Приоритет точных совпадений слова',
    modifier_boost: 'Вес слов-уточнений',
    concept_coverage_bonus_weight: 'Бонус за покрытие нескольких понятий',
    tag_match_weight: 'Вес совпадений по тегам',
    vector_similarity_weight: 'Вес смысловой похожести',
    tag_coverage_bonus_multiplier: 'Бонус за покрытие тегов',
    use_tag_aware_scoring: 'Учёт тегов в ранжировании',
    use_tier_scoring: 'Многоуровневое ранжирование',
    min_entities_for_tier_scoring: 'Минимум понятий для многоуровневого ранжирования',
    tier1_base_score: 'Уровень 1 — базовый балл',
    tier2_base_score: 'Уровень 2 — базовый балл',
    tier3_base_score: 'Уровень 3 — базовый балл',
    tier4_base_score: 'Уровень 4 — базовый балл',
    tier5_base_score: 'Уровень 5 — базовый балл',
    tier6_multiplier: 'Уровень 6 — множитель штрафа',
    opensearch_tier_post_filter_threshold: 'Порог отсечения (многоуровневое ранжирование)',
    tier1_min_vector_score: 'Уровень 1 — минимальная похожесть',
    tier2_min_vector_score: 'Уровень 2 — минимальная похожесть',
    tier3_min_vector_score: 'Уровень 3 — минимальная похожесть',
    tier4_min_vector_score: 'Уровень 4 — минимальная похожесть',
    tier5_min_vector_score: 'Уровень 5 — минимальная похожесть',
    tier6_min_vector_score: 'Уровень 6 — минимальная похожесть',
};

/**
 * Formats a setting's display name: prefer the Russian business label, else
 * fall back to auto Title Case from the snake_case key.
 * @param {string} key - Setting key in snake_case
 * @returns {string} Formatted display name
 */
function formatDisplayName(key) {
    if (RU_LABELS[key]) {
        return RU_LABELS[key];
    }
    return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Extracts numeric range from description text
 * Looks for patterns like "Range: 0.0-5.0", "Range: 1-10", "Range: 1000-120000"
 * @param {string} description - Setting description
 * @param {boolean} isFloat - Whether to parse as float or integer
 * @returns {Object|null} Object with min and max, or null if not found
 */
function extractRangeFromDescription(description, isFloat = false) {
    const rangeMatch = description.match(/Range:\s*([\d.]+)-([\d.]+)/i);
    if (rangeMatch) {
        const min = isFloat ? parseFloat(rangeMatch[1]) : parseInt(rangeMatch[1], 10);
        const max = isFloat ? parseFloat(rangeMatch[2]) : parseInt(rangeMatch[2], 10);
        return { min, max };
    }
    return null;
}

/**
 * Generates metadata for boolean type settings
 * @returns {Object} Metadata object
 */
function generateBooleanMetadata() {
    return {
        inputType: INPUT_TYPES.CHECKBOX,
        min: null,
        max: null,
        step: null,
        options: null,
    };
}

/**
 * Generates metadata for integer type settings
 * @param {Object} setting - Setting object with key and description
 * @returns {Object} Metadata object with range constraints
 */
function generateIntegerMetadata(setting) {
    const { key, description } = setting;
    const metadata = {
        inputType: INPUT_TYPES.NUMBER,
        step: STEP_VALUES.INTEGER,
        min: null,
        max: null,
        options: null,
    };

    // Try to extract explicit range from description first
    const intRange = extractRangeFromDescription(description, false);
    if (intRange) {
        metadata.min = intRange.min;
        metadata.max = intRange.max;
        return metadata;
    }

    // Fallback to key-based pattern matching
    if (KEY_PATTERNS.TIMEOUT_MS(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.TIMEOUT_MS);
    } else if (KEY_PATTERNS.TTL_SECONDS(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.TTL_SECONDS);
    } else if (KEY_PATTERNS.VECTOR_K(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.VECTOR_K);
    } else if (KEY_PATTERNS.THRESHOLD(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.THRESHOLD);
    } else if (KEY_PATTERNS.DOWNLOADS_LIMIT(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.DOWNLOADS_LIMIT);
    } else {
        Object.assign(metadata, RANGE_CONSTRAINTS.DEFAULT_INT);
    }

    return metadata;
}

/**
 * Generates metadata for float type settings
 * @param {Object} setting - Setting object with key and description
 * @returns {Object} Metadata object with range constraints
 */
function generateFloatMetadata(setting) {
    const { key, description } = setting;
    const metadata = {
        inputType: INPUT_TYPES.NUMBER,
        min: null,
        max: null,
        step: null,
        options: null,
    };

    // Try to extract explicit range from description first
    const floatRange = extractRangeFromDescription(description, true);
    if (floatRange) {
        metadata.min = floatRange.min;
        metadata.max = floatRange.max;
        // Determine step based on range
        metadata.step = floatRange.max <= 1.0 ? STEP_VALUES.SMALL_FLOAT : STEP_VALUES.LARGE_FLOAT;
        return metadata;
    }

    // Fallback to pattern-based detection
    if (DESCRIPTION_PATTERNS.RANGE_0_1(description) || KEY_PATTERNS.MIN_SCORE(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.SCORE_0_1);
        metadata.step = STEP_VALUES.SMALL_FLOAT;
    } else if (KEY_PATTERNS.WEIGHT(key) || KEY_PATTERNS.BOOST(key) ||
               KEY_PATTERNS.MULTIPLIER(key) || KEY_PATTERNS.FACTOR(key)) {
        Object.assign(metadata, RANGE_CONSTRAINTS.WEIGHT_BOOST);
        metadata.step = STEP_VALUES.LARGE_FLOAT;
    } else {
        Object.assign(metadata, RANGE_CONSTRAINTS.DEFAULT_FLOAT);
        metadata.step = STEP_VALUES.LARGE_FLOAT;
    }

    return metadata;
}

/**
 * Generates metadata for string type settings
 * @param {Object} setting - Setting object with description
 * @returns {Object} Metadata object with options or max length
 */
function generateStringMetadata(setting) {
    const { description } = setting;
    const metadata = {
        min: null,
        max: null,
        step: null,
        options: null,
    };

    // Try to extract enum values from description
    const enumMatch = description.match(/"([^"]+)"/g);
    if (enumMatch && enumMatch.length >= 2) {
        metadata.inputType = INPUT_TYPES.SELECT;
        metadata.options = enumMatch.map(opt => opt.replace(/"/g, ''));
    } else {
        metadata.inputType = INPUT_TYPES.TEXT;
        metadata.max = STRING_CONSTRAINTS.MAX_LENGTH;
    }

    return metadata;
}

/**
 * Generates metadata for a setting based on its type
 * @param {Object} setting - Setting object with type, key, and description
 * @returns {Object} Complete metadata object with displayName
 */
function generateSettingMetadata(setting) {
    const { type } = setting;
    let metadata;

    switch (type) {
        case 'boolean': {
            metadata = generateBooleanMetadata();
            break;
        }
        case 'integer': {
            metadata = generateIntegerMetadata(setting);
            break;
        }
        case 'float': {
            metadata = generateFloatMetadata(setting);
            break;
        }
        case 'string': {
            metadata = generateStringMetadata(setting);
            break;
        }
        default: {
            metadata = {
                inputType: INPUT_TYPES.TEXT,
                min: null,
                max: null,
                step: null,
                options: null,
            };
        }
    }

    metadata.displayName = formatDisplayName(setting.key);
    return metadata;
}

export default {
    state: {
        settings: [],
        isLoading: false,
        hasChanges: false,
        originalSettings: [],
    },

    getters: {
        SYSTEM_SETTINGS: (state) => state.settings,
        IS_SETTINGS_LOADING: (state) => state.isLoading,
        HAS_UNSAVED_CHANGES: (state) => state.hasChanges,
        ORIGINAL_SETTINGS: (state) => state.originalSettings,
    },

    mutations: {
        /**
         * Sets system settings with generated metadata
         */
        SET_SYSTEM_SETTINGS(state, payload) {
            state.settings = payload.map(setting => ({
                ...setting,
                metadata: generateSettingMetadata(setting)
            }));
        },
        /**
         * Sets loading state
         */
        SET_SETTINGS_LOADING(state, payload) {
            state.isLoading = payload;
        },
        /**
         * Sets original settings snapshot for change detection
         */
        SET_ORIGINAL_SETTINGS(state, payload) {
            state.originalSettings = payload;
        },
        /**
         * Sets whether there are unsaved changes
         */
        SET_HAS_CHANGES(state, payload) {
            state.hasChanges = payload;
        },
        /**
         * Updates a setting value and recalculates change status
         */
        UPDATE_SETTING_VALUE(state, { key, value }) {
            const setting = state.settings.find(s => s.key === key);
            if (!setting) return;

            // Convert value based on type before storing
            let normalizedValue = value;

            if (setting.type === 'integer') {
                const parsed = parseInt(value, 10);
                normalizedValue = isNaN(parsed) ? value : String(parsed);
            } else if (setting.type === 'float') {
                const parsed = parseFloat(value);
                normalizedValue = isNaN(parsed) ? value : String(parsed);
            } else if (setting.type === 'boolean') {
                normalizedValue = String(value);
            }

            setting.value = normalizedValue;

            // Inline hasSettingsChanged logic
            const { settings, originalSettings } = state;
            state.hasChanges = settings.length !== originalSettings.length ||
                settings.some((setting, idx) =>
                    setting.key !== originalSettings[idx].key ||
                    setting.value !== originalSettings[idx].value
                );
        },
    },

    actions: {
        /**
         * Loads system settings from API
         */
        async loadSystemSettings({ commit }) {
            commit('SET_SETTINGS_LOADING', true);
            try {
                const response = await getSystemSettings();
                if (!response?.settings) {
                    console.error('Invalid response format from getSystemSettings');
                    commit('SET_SYSTEM_SETTINGS', []);
                    commit('SET_ORIGINAL_SETTINGS', []);
                    return;
                }
                commit('SET_SYSTEM_SETTINGS', response.settings);
                commit('SET_ORIGINAL_SETTINGS', response.settings.map(s => ({ ...s })));
                commit('SET_HAS_CHANGES', false);
            } catch (error) {
                console.error('Failed to load system settings:', error);
                commit('SET_SYSTEM_SETTINGS', []);
                commit('SET_ORIGINAL_SETTINGS', []);
            } finally {
                commit('SET_SETTINGS_LOADING', false);
            }
        },

        /**
         * Saves modified settings to API
         */
        async saveSystemSettings({ commit, state }) {
            try {
                // Build update payload
                const settingsToUpdate = state.settings.map(setting => ({
                    key: setting.key,
                    value: setting.value
                }));

                const response = await updateSystemSettings(settingsToUpdate);

                if (response?.settings) {
                    commit('SET_SYSTEM_SETTINGS', response.settings);
                    commit('SET_ORIGINAL_SETTINGS', response.settings.map(s => ({ ...s })));
                    commit('SET_HAS_CHANGES', false);
                    return true;
                }

                console.error('Invalid response format from updateSystemSettings');
                return false;
            } catch (error) {
                console.error('Failed to save system settings:', error);
                return false;
            }
        },

        /**
         * Resets all settings to their default values
         */
        async resetToDefaults({ commit, state }) {
            try {
                // Build update payload with default values
                const settingsToUpdate = state.settings.map(setting => ({
                    key: setting.key,
                    value: DEFAULT_SETTINGS[setting.key] || setting.value
                }));

                const response = await updateSystemSettings(settingsToUpdate);

                if (response?.settings) {
                    commit('SET_SYSTEM_SETTINGS', response.settings);
                    commit('SET_ORIGINAL_SETTINGS', response.settings.map(s => ({ ...s })));
                    commit('SET_HAS_CHANGES', false);
                    return true;
                }

                console.error('Invalid response format from updateSystemSettings');
                return false;
            } catch (error) {
                console.error('Failed to reset system settings to defaults:', error);
                return false;
            }
        },

        /**
         * Reverts settings to last saved state
         */
        resetToOriginal({ commit, state }) {
            commit('SET_SYSTEM_SETTINGS', state.originalSettings.map(s => ({ ...s })));
            commit('SET_HAS_CHANGES', false);
        },

    },
};
